'use client';

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePathname, useRouter } from 'next/navigation';
import { ShoppingBagIcon, XMarkIcon } from '@heroicons/react/24/outline';

import Button from '@ui/Button';

import { storeFront } from '@/lib/utils/routeLinks';
import { LastFailedOrder } from '@/lib/types/storage-types';
import {
  getFailedOrderInfo,
  removeFailedOrderInfo,
  showFailedOrderPopup,
} from '@/lib/utils/storage';
import { useTranslations } from 'next-intl';

export default function FailedOrderPopup() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState<boolean>(false);

  const path = usePathname();
  const router = useRouter();

  const isPaymentFailedPage = path.includes(
    storeFront.checkout.de() || storeFront.checkout.en()
  );

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    removeFailedOrderInfo();
    setIsVisible(false);
  };

  const t = useTranslations();

  const checkOrderExpiry = (expiryDateString: string): boolean => {
    const expiryTimestamp = new Date(expiryDateString).getTime();
    const currentTimestamp = new Date().getTime();
    const isExpired = currentTimestamp > expiryTimestamp;

    if (isExpired) {
      removeFailedOrderInfo();
      setIsVisible(false);
    }

    return isExpired;
  };

  const handleReorder = () => {
    const {
      order_id,
      token,
      email,
      guest_order,
      order_token,
      expires_at,
    }: LastFailedOrder = getFailedOrderInfo();

    if (expires_at && checkOrderExpiry(expires_at)) {
      removeFailedOrderInfo();
      setIsVisible(false);
      return;
    }

    let reorderPageURL = '';
    if (guest_order) {
      reorderPageURL =
        storeFront.checkout.de() +
        `/callback/failed?order_id=${order_id}&guest_order=yes&order_token=${order_token}&token=${token}&email=${email}`;
    } else {
      reorderPageURL =
        storeFront.checkout.de() +
        `/callback/failed?order_id=${order_id}&guest_order=no&order_token=${order_token}`;
    }
    router.push(reorderPageURL);
  };

  useEffect(() => {
    const checkAndUpdateVisibility = () => {
      const failedOrderInfo = getFailedOrderInfo();
      if (!failedOrderInfo?.expires_at) {
        setIsVisible(false);
        return;
      }

      const expiryTimestamp = new Date(failedOrderInfo.expires_at).getTime();
      const currentTimestamp = new Date().getTime();
      const timeRemaining = expiryTimestamp - currentTimestamp;

      const failedOrderExists = showFailedOrderPopup();
      const orderNotExpired = timeRemaining > 0;
      const shouldBeVisible =
        failedOrderExists && !isPaymentFailedPage && orderNotExpired;

      setIsVisible(shouldBeVisible);

      if (!shouldBeVisible) {
        removeFailedOrderInfo();
      }
    };

    checkAndUpdateVisibility();

    const intervalId = setInterval(checkAndUpdateVisibility, 60000);

    const failedOrderInfo = getFailedOrderInfo();
    if (failedOrderInfo?.expires_at) {
      const expiryTimestamp = new Date(failedOrderInfo.expires_at).getTime();
      const currentTimestamp = new Date().getTime();
      const timeRemaining = Math.max(0, expiryTimestamp - currentTimestamp);

      const timeoutId = setTimeout(() => {
        removeFailedOrderInfo();
        setIsVisible(false);
      }, timeRemaining);

      return () => {
        clearInterval(intervalId);
        clearTimeout(timeoutId);
      };
    }

    return () => {
      clearInterval(intervalId);
    };
  }, [isPaymentFailedPage]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="fixed bottom-24 right-4 z-50"
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 20 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            layout
            className={`relative cursor-pointer overflow-hidden rounded-lg bg-primary-800 text-white shadow-lg`}
            initial={{ width: 220, height: 48 }}
            animate={{
              width: isExpanded ? 320 : 220,
              height: isExpanded ? 200 : 48,
            }}
            transition={{
              duration: 0.3,
              type: 'spring',
              stiffness: 300,
              damping: 30,
            }}
            onHoverStart={() => setIsExpanded(true)}
            onHoverEnd={() => setIsExpanded(false)}
            whileHover={{ scale: 1.05 }}
          >
            <AnimatePresence>
              {isExpanded && (
                <motion.button
                  className="absolute right-3 top-3 text-slate-300 transition-colors hover:text-white"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={handleClose}
                  aria-label={t('aria.close_reorder_popup_notification')}
                >
                  <XMarkIcon className="h-4 w-4" />
                </motion.button>
              )}
            </AnimatePresence>
            <motion.div
              className="flex h-full w-full flex-col items-center justify-center p-4"
              layout
            >
              <motion.div className="my-4 flex items-center gap-2" layout>
                <ShoppingBagIcon className="h-6 w-6 flex-shrink-0" />
                <span className="whitespace-nowrap text-base font-semibold">
                  {t('storefront.checkout.reorder')}
                </span>
              </motion.div>
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2, delay: 0.1 }}
                    className="mt-2 w-full text-center"
                  >
                    <p className="mb-4 px-2 text-sm leading-tight">
                      {t('storefront.checkout.try_again')}
                    </p>
                    <Button
                      className="w-full bg-primary/80 py-2.5 text-sm font-medium"
                      onClick={() => handleReorder()}
                    >
                      {t('storefront.checkout.reorder_failed_items')}
                    </Button>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
